import ContactForm from "@/components/ContactForm";
import FormCard from "@/components/FormCard";
import { Phone, Mail, MapPin } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import AnimatedCounter from "@/components/AnimatedCounter";
import DynamicGradient from "@/components/ui/DynamicGradient";

export default function ContactSection() {
  const { t } = useTranslation('home');
  const [headerVisible, setHeaderVisible] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const headerObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setHeaderVisible(true);
      },
      { threshold: 0.5 }
    );
    const statsObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStatsVisible(true);
      },
      { threshold: 0.3 }
    );
    if (headerRef.current) headerObserver.observe(headerRef.current);
    if (statsRef.current) statsObserver.observe(statsRef.current);
    return () => {
      headerObserver.disconnect();
      statsObserver.disconnect();
    };
  }, []);

  return <DynamicGradient className="py-20 bg-white dark:bg-slate-900" variant="secondary">
      <section id="contact">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Emergency Services Heading */}


          <div
            ref={headerRef}
            className={`text-center mb-12 mt-16 transition-all duration-1000 ease-out ${
              headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <span className="text-primary font-extrabold tracking-[0.2em] uppercase text-xs mb-4 block">{t('contact.eyebrow')}</span>
            <h2 className="text-secondary dark:text-white text-4xl md:text-5xl lg:text-6xl font-heading font-black uppercase tracking-tight mb-6">{t('contact.headline')}</h2>
            <p
              className={`text-xl text-muted-foreground max-w-2xl mx-auto transition-all duration-1000 ease-out delay-200 ${
                headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
            >
              {t('contact.subheadline')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Contact Information */}
            <div className="space-y-8">
              {/* Trust Indicators - Centered stats with accent lines */}
              <div ref={statsRef} className="flex flex-wrap justify-center gap-12 md:gap-16 pb-8">
                <div
                  className="text-center"
                  style={{
                    opacity: statsVisible ? 1 : 0,
                    transform: statsVisible ? "translateY(0)" : "translateY(10px)",
                    transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
                    transitionDelay: "0ms",
                  }}
                >
                  <div className="text-4xl md:text-5xl font-heading font-bold text-primary leading-none">
                    <AnimatedCounter end={7000} suffix="+" startOnView={false} />
                  </div>
                  <div className="w-8 h-0.5 bg-primary mx-auto mt-2 mb-1" />
                  <div className="text-sm text-muted-foreground font-medium">{t('contact.stats.roofsByCrew')}</div>
                </div>
                <div
                  className="text-center"
                  style={{
                    opacity: statsVisible ? 1 : 0,
                    transform: statsVisible ? "translateY(0)" : "translateY(10px)",
                    transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
                    transitionDelay: "100ms",
                  }}
                >
                  <div className="text-4xl md:text-5xl font-heading font-bold text-primary leading-none">
                    <AnimatedCounter end={15} suffix="+" startOnView={false} />
                  </div>
                  <div className="w-8 h-0.5 bg-primary mx-auto mt-2 mb-1" />
                  <div className="text-sm text-muted-foreground font-medium">{t('contact.stats.yearsInHouston')}</div>
                </div>
                <div
                  className="text-center"
                  style={{
                    opacity: statsVisible ? 1 : 0,
                    transform: statsVisible ? "translateY(0)" : "translateY(10px)",
                    transition: "all 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
                    transitionDelay: "200ms",
                  }}
                >
                  <div className="text-4xl md:text-5xl font-heading font-bold text-primary leading-none">24/7</div>
                  <div className="w-8 h-0.5 bg-primary mx-auto mt-2 mb-1" />
                  <div className="text-sm text-muted-foreground font-medium">{t('contact.stats.emergencyService')}</div>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold mb-6">{t('contact.info.title')}</h3>
                <div className="space-y-4">
                  <motion.div
                    className="flex items-start gap-4"
                    whileHover={{ x: 4 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Phone className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold mb-1">{t('contact.info.phone')}</h4>
                      <a href="tel:+13462200989" className="text-muted-foreground hover:text-primary transition-colors">
                        (346) 220-0989
                      </a>
                    </div>
                  </motion.div>

                  <motion.div
                    className="flex items-start gap-4"
                    whileHover={{ x: 4 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Mail className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold mb-1">{t('contact.info.email')}</h4>
                      <a href="mailto:Contact@altumcontractors.com" className="text-muted-foreground hover:text-primary transition-colors">
                        Contact@altumcontractors.com
                      </a>
                    </div>
                  </motion.div>

                  <motion.div
                    className="flex items-start gap-4"
                    whileHover={{ x: 4 }}
                    transition={{ duration: 0.2 }}
                  >
                    <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold mb-1">{t('contact.info.serviceArea')}</h4>
                      <p className="text-muted-foreground">
                        {t('contact.info.serviceAreaText')}
                      </p>
                    </div>
                  </motion.div>
                </div>
              </div>

              <div className="border-l-2 border-primary pl-4">
                <h4 className="font-semibold mb-2">{t('contact.info.hours')}</h4>
                <p className="text-sm text-muted-foreground">
                  {t('contact.info.hoursWeekday')}<br />
                  {t('contact.info.hoursSaturday')}<br />
                  {t('contact.info.hoursSunday')}<br />
                  <span className="text-primary font-medium">{t('contact.info.emergency247')}</span>
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <FormCard title={t('contact.formTitle')}>
                <ContactForm />
              </FormCard>
            </motion.div>
          </div>
        </div>
      </div>
      </section>
    </DynamicGradient>;
}